import { Component, OnDestroy, OnInit } from '@angular/core';
import { QueryRef } from 'apollo-angular';

import { SubSink } from 'subsink';

import { GetRollingLockDateConfigsGQL, GetRollingLockDateConfigsQuery, GetRollingLockDateConfigsQueryVariables, SetConfigValuesGQL, ZoneConfigKey } from '../../../generated/graphql.generated';
import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';

interface RollingLockDateTableRow {
    name: string;
    value: string | number;
    zone: string;
    key: string;
    dataType: 'date' | 'cron';
}

@Component({
    selector: 'app-accounting',
    templateUrl: './accounting.component.html',
    styleUrls: ['./accounting.component.scss']
})
export class AccountingComponent implements OnInit, OnDestroy {

    configValuesQueryRef: QueryRef<GetRollingLockDateConfigsQuery, GetRollingLockDateConfigsQueryVariables>;

    subs = new SubSink();

    rows: RollingLockDateTableRow[] = [];

    hasPermissionToModifyLockDate = [{
        permission: 'config.set',
        restriction: { canModifyLockDate: true },
    }];

    configs: GetRollingLockDateConfigsQuery['getZoneConfigs'] = {};

    currentDate: number;

    supportEmail: string;

    contactAccountingButtonText: string;

    emailSubject: string;

    emailBodyPrefix: string;

    emailBodySuffix: string;

    eventLockedWarning: string;

    loading = false;

    dummyJobCode = 'MOVE-8N5-PAA';

    sampleInfo = `
        Job: ${this.dummyJobCode}
        Customer: Smith, John
        Event: Moving (Booked), June 27
        Link: [link to job]
        User making the request: Doe, John
    `;

    templatePreview = '';

    resetChildZones = true;

    constructor(
        private getRollingLockDateConfigsGQL: GetRollingLockDateConfigsGQL,
        private detailsHelper: DetailsHelperService,
        private setConfigGQL: SetConfigValuesGQL,
        private localNotify: FreyaNotificationsService,
    ) {}

    ngOnInit(): void {
        this.fetchConfigValues();

        this.subs.sink = this.detailsHelper.getObjectUpdates('Configs').subscribe((update) => {
            if (update.action === 'update') {
                this.fetchConfigValues();
            }
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    fetchConfigValues() {

        const keys = [
            ZoneConfigKey.RollingLockDateCurrentdate,
            ZoneConfigKey.RollingLockDateSupportemail,
            ZoneConfigKey.RollingLockDateEmailsubject,
            ZoneConfigKey.RollingLockDateContactaccountingbuttontext,
            ZoneConfigKey.RollingLockDateEventlockedwarning,
            ZoneConfigKey.RollingLockDateEmailbodyprefix,
            ZoneConfigKey.RollingLockDateEmailbodysuffix,
        ];

        if (this.configValuesQueryRef) {
            this.configValuesQueryRef.refetch();
            return;
        }

        this.configValuesQueryRef = this.getRollingLockDateConfigsGQL.watch({ keys }, { notifyOnNetworkStatusChange: true });

        this.subs.sink = this.configValuesQueryRef.valueChanges.subscribe((res) => {
            this.loading = res.loading;
            if (res?.loading) { return; }

            const configValues = res.data.getZoneConfigs;

            this.currentDate = configValues.ROLLING__LOCK__DATE_CURRENTDATE?.value;

            this.supportEmail = configValues.ROLLING__LOCK__DATE_SUPPORTEMAIL?.value;

            this.contactAccountingButtonText = configValues.ROLLING__LOCK__DATE_CONTACTACCOUNTINGBUTTONTEXT?.value;

            this.emailSubject = configValues.ROLLING__LOCK__DATE_EMAILSUBJECT?.value;

            this.emailBodyPrefix = configValues.ROLLING__LOCK__DATE_EMAILBODYPREFIX?.value;

            this.emailBodySuffix = configValues.ROLLING__LOCK__DATE_EMAILBODYSUFFIX?.value;

            this.eventLockedWarning = configValues.ROLLING__LOCK__DATE_EVENTLOCKEDWARNING?.value;

            const configMap: typeof this.configs = {};

            for (const key of keys) {
                configMap[key] = res.data.getZoneConfigs[key];
            }

            this.configs = configMap;

            this.setTemplatePreview();
        });
    }

    /**
     * Updates a config value, then deactivates the "edit in place" component
     * by calling the component's deactivate() method.
     */
    updateConfig(
      subKey: string,
      value: string | number,
      deactivate: () => void,
    ) {

        const key = `rolling-lock-date.${subKey}`;
        const uncamelizedKey = subKey.split(/(?=[A-Z])/).join(' ').toLowerCase();
        const successMsg = `Successfully updated ${uncamelizedKey}`;
        const failMsg = `Failed to update ${uncamelizedKey}`;

        const resetOverridingConfigs = subKey === 'currentDate' && this.resetChildZones;

        this.setConfigGQL.mutate({ configs: [ {
          key,
          value: typeof value === 'number' ? value.toString() : value,
          resetOverridingConfigs,
        } ] })
            .subscribe(() => {
                this.localNotify.success(successMsg);
                deactivate();
                this[subKey] = value;
                this.setTemplatePreview();

                if (resetOverridingConfigs) {
                    setTimeout(() => {
                        this.detailsHelper.pushUpdate({
                            type: 'Configs',
                            action: 'update',
                            id: '',
                        });
                    }, 0);
                }
            }, (err) => {
                this.localNotify.apolloError(failMsg, err);
                deactivate();
            });
    }

    setTemplatePreview() {

        const lineBreak = '\n';
        const doubleLineBreak = '\n\n';

        let preview = `to: ${this.supportEmail || 'No email address configured'}`;

        const subject = this.emailSubject || 'No subject configured';

        preview += lineBreak + 'Subject: ' + `${this.dummyJobCode}: ${ subject }`;

        if (this.emailBodyPrefix) {
            preview += doubleLineBreak + this.emailBodyPrefix;
        }

        preview += doubleLineBreak + this.sampleInfo;

        if (this.emailBodySuffix) {
            preview += doubleLineBreak + this.emailBodySuffix;
        }

        this.templatePreview = preview;
    }
}
